import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { bookingApi } from '../../../utils/bookingApi';
import { formatDate } from '../../../utils/dates';
import { useAuth } from '../../../contexts/AuthContext';
import { PageHeader, Sheet, Skeleton, ErrorState, InlineError } from '../../../components/ui';
import '../booking-shared.css';
import './AdminMemberPayments.css';

function pence(n) {
  return `£${(n / 100).toFixed(2)}`;
}

// How each payment turned out, as a status label tone
const RESULT_TONE = { succeeded: 'good', failed: 'bad', due: 'wait', cancelled: 'off' };

function ResultPill({ payment }) {
  return <span className={`bk-pill bk-pill--${RESULT_TONE[payment.result] || 'off'}`}>{payment.statusLabel || 'Unknown'}</span>;
}

// The app's own receipt (or, for a payment that didn't go through, its details).
// Printing it gives the member a copy; nothing here links to the card processor.
function Receipt({ payment, member, clubName, onClose }) {
  const paid = payment.result === 'succeeded';
  return (
    <Sheet
      title={paid ? 'Receipt' : 'Payment details'}
      onClose={onClose}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="button" className="bk-btn" onClick={() => window.print()}>Print</button>
          <button type="button" className="bk-btn bk-btn--primary" onClick={requestClose}>Close</button>
        </div>
      )}
    >
      <div className="mpay-receipt">
        <div className="mpay-receipt__head">
          <div>
            <div className="mpay-receipt__club">{clubName}</div>
            <div className="mpay-receipt__title">{paid ? 'Receipt' : 'Payment details'}</div>
          </div>
          <ResultPill payment={payment} />
        </div>
        <dl className="mpay-receipt__meta">
          <div><dt>For</dt><dd>{member.firstName} {member.lastName}</dd></div>
          <div><dt>Date</dt><dd>{formatDate(payment.date)}</dd></div>
          <div><dt>What for</dt><dd>{payment.source}</dd></div>
          {payment.reference && <div><dt>Reference</dt><dd>{payment.reference}</dd></div>}
        </dl>
        <table className="mpay-receipt__lines">
          <tbody>
            {(payment.lines?.length ? payment.lines : [{ description: payment.description, amount: payment.amount }]).map((l, i) => (
              <tr key={i}><td>{l.description}</td><td>{l.amount != null ? pence(l.amount) : ''}</td></tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td>{paid ? 'Total paid' : 'Total'}</td><td>{pence(payment.amount)}</td></tr>
          </tfoot>
        </table>
      </div>
    </Sheet>
  );
}

export default function AdminMemberPayments() {
  const { user } = useAuth();
  const [receipt, setReceipt] = useState(null);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [payments, setPayments] = useState([]);
  const [pm, setPm] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const detailRef = useRef(null);

  const loadMembers = useCallback(() => {
    setError(null);
    setLoadingMembers(true);
    bookingApi.getPayers()
      .then(res => setMembers(res.data || []))
      .catch(err => setError(err.response?.data?.error || ''))
      .finally(() => setLoadingMembers(false));
  }, []);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...members].sort((a, b) =>
      `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)
    );
    if (!q) return sorted;
    // A child's name finds the adult who pays for them
    return sorted.filter(m =>
      `${m.firstName} ${m.lastName}`.toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q) ||
      (m.children || []).some(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(q))
    );
  }, [members, search]);

  const selectedMember = useMemo(
    () => members.find(m => m.id === selectedId) || null,
    [members, selectedId]
  );

  useEffect(() => {
    if (!selectedId) {
      setPayments([]); setPm(null);
      return undefined;
    }
    // Clear the last member's details so a failed load never shows them
    // under this member's name, and ignore replies for a member no longer picked
    let current = true;
    setPayments([]); setPm(null); setDetailError(null);
    setLoadingDetail(true);
    Promise.all([
      bookingApi.getMemberPayments(selectedId),
      bookingApi.getMemberPaymentMethod(selectedId),
    ])
      .then(([paymentsRes, pmRes]) => {
        if (!current) return;
        setPayments(paymentsRes.data.payments || []);
        setPm(pmRes.data.paymentMethod || null);
      })
      .catch(err => { if (current) setDetailError(err.response?.data?.error || "Couldn't load the payment history."); })
      .finally(() => { if (current) setLoadingDetail(false); });
    // On a phone the payments are below the member list, so bring them into view
    if (window.matchMedia?.('(max-width: 768px)').matches) {
      detailRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
    return () => { current = false; };
  }, [selectedId, attempt]);

  // Only money that actually came in counts towards the total
  const paidPayments = payments.filter(p => p.result === 'succeeded');
  const total = paidPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const failedCount = payments.filter(p => p.result === 'failed').length;

  return (
    <div className="bk-page bk-page--xl">
      <PageHeader
        back={{ to: '/admin-hub', label: 'Admin' }}
        title="Member payments"
        details="Members who have paid for something in the last 12 months. Search by their name or their child's, then pick one to see their payments for memberships, bookings, the shop and admin charges, whether each one went through, and a receipt for it."
      />

      {error !== null && <ErrorState title="Couldn't load members" text={error || undefined} onRetry={loadMembers} />}

      {error === null && (
        <div className="mpay">
          <aside className="bk-card mpay__side">
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by member or child…"
              aria-label="Search members"
              className="bk-input mpay__search"
            />
            {loadingMembers ? (
              <Skeleton rows={4} label="Loading members…" />
            ) : (
              <div className="mpay__list">
                {filteredMembers.map(m => {
                  const isSelected = m.id === selectedId;
                  return (
                    <button key={m.id} type="button" className="mpay__member" aria-pressed={isSelected} onClick={() => setSelectedId(m.id)}>
                      <span className="mpay__member-name">{m.firstName} {m.lastName}</span>
                      {m.email && <span className="mpay__member-sub">{m.email}</span>}
                      {m.children?.length > 0 && (
                        <span className="mpay__member-sub">Pays for {m.children.map(c => c.firstName).join(', ')}</span>
                      )}
                    </button>
                  );
                })}
                {filteredMembers.length === 0 && (
                  <p className="bk-muted mpay__empty">
                    {search.trim() ? 'No one who has paid in the last 12 months matches.' : 'No one has paid for anything in the last 12 months.'}
                  </p>
                )}
              </div>
            )}
          </aside>

          <section className="mpay__detail" ref={detailRef}>
            {!selectedMember && (
              <div className="bk-card">
                <p className="bk-muted" style={{ margin: 0 }}>Pick a member from the list to see their payment history.</p>
              </div>
            )}

            {selectedMember && (
              <>
                <div className="bk-card mpay__who">
                  <h2 className="mpay__name">{selectedMember.firstName} {selectedMember.lastName}</h2>
                  {selectedMember.email && <p className="bk-muted" style={{ margin: '0.25rem 0 0' }}>{selectedMember.email}</p>}
                  <p className="mpay__card">
                    <strong>Card on file: </strong>
                    {pm ? (
                      <span>
                        {pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)} ending {pm.last4}
                        <span className="bk-muted"> · Expires {String(pm.expMonth).padStart(2, '0')}/{String(pm.expYear).slice(-2)}</span>
                      </span>
                    ) : (
                      <span className="bk-muted">None</span>
                    )}
                  </p>
                </div>

                <div className="bk-card">
                  <div className="mpay__head">
                    <h3 className="bk-section-title" style={{ margin: 0 }}>Recent payments</h3>
                    <span className="bk-muted bk-small">
                      {paidPayments.length} paid · {pence(total)}{failedCount > 0 ? ` · ${failedCount} didn't go through` : ''}
                    </span>
                  </div>

                  {loadingDetail && <Skeleton rows={3} label="Loading payments…" />}
                  {!loadingDetail && detailError && <InlineError onRetry={() => setAttempt(a => a + 1)}>{detailError}</InlineError>}
                  {!loadingDetail && !detailError && payments.length === 0 && (
                    <p className="bk-muted" style={{ margin: 0 }}>No payments in the last 12 months.</p>
                  )}
                  {!loadingDetail && payments.length > 0 && (
                    <div className="mpay__table-wrap">
                      <table className="mpay__table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>For</th>
                            <th>Description</th>
                            <th className="num">Amount</th>
                            <th>Status</th>
                            <th><span className="mpay__sr">Receipt</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map(p => (
                            <tr key={p.id}>
                              <td className="mpay__date">{formatDate(p.date)}</td>
                              <td className="mpay__source"><span className="bk-pill bk-pill--off">{p.source || 'Other'}</span></td>
                              <td className="mpay__desc" title={p.description}>
                                {p.description.length > 70 ? `${p.description.slice(0, 67)}…` : p.description}
                              </td>
                              <td className="mpay__amount num">{pence(p.amount)}</td>
                              <td className="mpay__status"><ResultPill payment={p} /></td>
                              <td className="mpay__links">
                                <button type="button" className="bk-btn bk-btn--sm" onClick={() => setReceipt(p)}>
                                  {p.result === 'succeeded' ? 'Receipt' : 'Details'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      )}
      {receipt && selectedMember && (
        <Receipt payment={receipt} member={selectedMember} clubName={user?.club?.name || ''} onClose={() => setReceipt(null)} />
      )}
    </div>
  );
}
